import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useStore from "../store/useStore";
import HeaderStrip from "./Header/HeaderStrip";
import SearchBar from "./Header/SearchBar";
import LocationSelector from "./Header/LocationSelector";
import CartButton from "./Header/CartButton";
import WishlistNavButton from "./Header/WishlistNavButton";
import UserProfile from "./Header/UserProfile";
import CategoryDropdown from "./Header/CategoryDropdown";
import useBodyScrollLock from "../utils/useBodyScrollLock";
import {
  FaBoxOpen,
  FaHeart,
  FaHome,
  FaInfoCircle,
  FaLeaf,
  FaPhoneAlt,
  FaShoppingCart,
  FaSyncAlt,
  FaTruck,
  FaUndo,
  FaUser,
  FaMapMarkerAlt,
  FaBell,
  FaGift,
} from "react-icons/fa";

const Header = () => {
  const {
    searchQuery,
    setSearchQuery,
    clearSearch,
    setSelectedCategory,
    setSelectedCategoryData,
    categories,
    fetchCategories,
    fetchCart,
    fetchWishlist,
    cart_count,
    isAuthenticated,
    user,
    logout,
    openAuthModal,
    userLocation,
    user_addresses,
    openLocationModal,
    serverNotifications,
    fetchNotifications,
  } = useStore();

  const unreadNotificationsCount = (serverNotifications || []).filter(
    (n) => !n.is_read
  ).length;

  // Derive a human-readable location label from the resolved location
  const defaultAddress = user_addresses?.find((address) => address.is_default);
  const locationLabel = defaultAddress
    ? (defaultAddress.city || defaultAddress.pincode || 'My Location')
    : userLocation
      ? (userLocation.area || userLocation.city || userLocation.pincode || 'My Location')
    : null;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const drawerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!location.pathname.startsWith("/products")) {
      clearSearch();
    }
  }, [location.pathname, clearSearch]);

  useEffect(() => {
    if (!categories.length) fetchCategories();
  }, [categories.length, fetchCategories]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
      if (typeof fetchWishlist === 'function') fetchWishlist();
      if (typeof fetchNotifications === 'function') fetchNotifications({ page: 1, limit: 10 });
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Close drawer on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target))
        setMobileOpen(false);
    };
    if (mobileOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mobileOpen]);

  useBodyScrollLock(mobileOpen);

  const go = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogoClick = () => {
    setSelectedCategory("all");
    if (setSelectedCategoryData) setSelectedCategoryData(null);
    if (setSearchQuery) setSearchQuery("");
    window.scrollTo({ top: 0, behavior: "smooth" });
    go("/");
  };

  const handleCategoryClick = (slug) => {
    if (setSearchQuery) setSearchQuery("");
    const targetSlug = slug || "all";
    setSelectedCategory(targetSlug);
    if (setSelectedCategoryData) {
      const catData = categories.find((c) => c.slug === targetSlug);
      setSelectedCategoryData(catData || null);
    }
    if (targetSlug === "all") {
      go("/products");
    } else {
      go(`/products?category=${targetSlug}`);
    }
  };

  const handleAccountNav = (path) => {
    if (!isAuthenticated) {
      openAuthModal();
      setMobileOpen(false);
    } else {
      go(path);
    }
  };

  const initials = user?.name
    ? user.name.slice(0, 2).toUpperCase()
    : user?.mobile
      ? user.mobile.slice(-2)
      : "U";

  return (
    <>
      <header className="sticky top-0 z-[60] w-full border-b border-gray-100 bg-white shadow-sm">
        <HeaderStrip />

        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 py-2 lg:gap-6">
            {/* Logo */}
            <div
              className="flex shrink-0 cursor-pointer flex-col items-center"
              onClick={handleLogoClick}
            >
              <img
                src="/k_kisanlogo.png"
                alt="KKisan"
                className="h-12 w-auto sm:h-14 lg:h-16"
              />
              <span className="mt-0.5 text-[10px] font-bold leading-none text-primary-700 sm:text-xs">
                Kisan Daily Fresh
              </span>
            </div>

            {/* Search — grows to fill space */}
            <div className="flex-1">
              <SearchBar value={searchQuery} onChange={setSearchQuery} />
            </div>

            {/* Desktop nav */}
            <div className="hidden items-center gap-3 lg:flex">
              <CategoryDropdown
                categories={categories}
                onCategoryClick={handleCategoryClick}
              />
              <LocationSelector
                value={locationLabel || 'Set Location'}
                onChange={openLocationModal}
                disabled={Boolean(defaultAddress)}
              />
              <WishlistNavButton className="h-11 w-11 p-2.5" />
              <CartButton
                count={cart_count}
                onClick={() => go("/cart")}
                className="h-11 w-11 p-2.5"
              />
              <UserProfile
                isAuthenticated={isAuthenticated}
                user={user}
                onLogout={() => {
                  logout();
                  go("/");
                }}
                onLogin={openAuthModal}
              />
            </div>

            {/* Mobile right icons */}
            <div className="flex items-center gap-1.5 lg:hidden">
              <WishlistNavButton className="h-10 w-10 p-2" />
              <CartButton
                count={cart_count}
                onClick={() => go("/cart")}
                className="h-10 w-10 p-2"
              />
              {/* Hamburger */}
              <button
                onClick={() => setMobileOpen((p) => !p)}
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-50 text-gray-700 transition hover:bg-primary-50 hover:text-primary-600"
                aria-label="Menu"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={
                      mobileOpen
                        ? "M6 18L18 6M6 6l12 12"
                        : "M4 6h16M4 12h16M4 18h16"
                    }
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Mobile drawer overlay ── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[70] overflow-hidden bg-black/40 backdrop-blur-sm lg:hidden">
          {/* Drawer panel */}
          <div
            ref={drawerRef}
            className="absolute right-0 top-0 flex h-full w-[310px] max-w-[85vw] flex-col overflow-hidden bg-white shadow-2xl"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between border-b border-gray-100 bg-gradient-to-r from-primary-600 to-primary-700 px-5 py-3">
              <div
                className="flex items-center gap-2.5 cursor-pointer"
                onClick={handleLogoClick}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white p-1 shadow">
                  <img
                    src="/k_kisanlogo.png"
                    alt="Kisan Daily Fresh"
                    className="h-full w-full object-contain"
                    onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                  />
                  <span style={{ display: 'none' }} className="flex h-full w-full items-center justify-center text-xs font-extrabold text-primary-700">KF</span>
                </div>
                <div>
                  <p className="text-sm font-extrabold leading-tight text-white">Kisan Daily Fresh</p>
                  <p className="text-[10px] font-semibold leading-tight text-primary-200">Farm to Doorstep</p>
                </div>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* User info strip */}
            <div className="border-b border-gray-100 bg-primary-50 px-5 py-3">
              {isAuthenticated ? (
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-sm font-extrabold text-white">
                    {initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-gray-900">
                      {user?.name || "My Account"}
                    </p>
                    <p className="truncate text-xs text-gray-500">
                      {user?.mobile || user?.email || ""}
                    </p>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    openAuthModal();
                    setMobileOpen(false);
                  }}
                  className="w-full rounded-xl bg-primary-600 py-2.5 text-sm font-bold text-white shadow hover:bg-primary-700"
                >
                  Login / Sign Up
                </button>
              )}
            </div>

            {/* ── Mobile Quick Tabs: My Address, Notification, Referral ── */}
            <div className="border-b border-gray-100 bg-gray-50/70 p-3">
              <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                Account Shortcuts
              </p>
              <div className="grid grid-cols-3 gap-2">
                {/* 1. My Address */}
                <button
                  type="button"
                  onClick={() => handleAccountNav("/account/addresses")}
                  className="group flex flex-col items-center justify-center rounded-2xl border border-gray-200/80 bg-white p-2.5 text-center shadow-xs transition hover:border-[#046a38] hover:bg-emerald-50/40 active:scale-95"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-50 text-[#046a38] transition group-hover:bg-[#046a38] group-hover:text-white shadow-xs">
                    <FaMapMarkerAlt className="h-3.5 w-3.5" />
                  </div>
                  <span className="mt-1.5 text-[11px] font-bold text-gray-800 leading-tight">
                    My Address
                  </span>
                  <span className="text-[9px] font-medium text-gray-400">
                    Locations
                  </span>
                </button>

                {/* 2. Notification */}
                <button
                  type="button"
                  onClick={() => handleAccountNav("/account/notifications")}
                  className="group relative flex flex-col items-center justify-center rounded-2xl border border-gray-200/80 bg-white p-2.5 text-center shadow-xs transition hover:border-blue-500 hover:bg-blue-50/40 active:scale-95"
                >
                  {unreadNotificationsCount > 0 && (
                    <span className="absolute right-1.5 top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#046a38] px-1 text-[9px] font-black text-white ring-1 ring-white">
                      {unreadNotificationsCount}
                    </span>
                  )}
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition group-hover:bg-blue-600 group-hover:text-white shadow-xs">
                    <FaBell className="h-3.5 w-3.5" />
                  </div>
                  <span className="mt-1.5 text-[11px] font-bold text-gray-800 leading-tight">
                    Notifications
                  </span>
                  <span className="text-[9px] font-medium text-gray-400">
                    {unreadNotificationsCount > 0 ? `${unreadNotificationsCount} new` : "Updates"}
                  </span>
                </button>

                {/* 3. Referral */}
                <button
                  type="button"
                  onClick={() => handleAccountNav("/account/referral")}
                  className="group relative flex flex-col items-center justify-center rounded-2xl border border-amber-200/70 bg-gradient-to-b from-amber-50/40 to-white p-2.5 text-center shadow-xs transition hover:border-amber-400 hover:bg-amber-50/60 active:scale-95"
                >
                  <span className="absolute -top-1.5 right-1 rounded-full bg-amber-500 px-1.5 py-0.2 text-[8px] font-black uppercase text-white shadow-xs">
                    Earn ₹
                  </span>
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-amber-100/80 text-amber-700 transition group-hover:bg-amber-500 group-hover:text-white shadow-xs">
                    <FaGift className="h-3.5 w-3.5" />
                  </div>
                  <span className="mt-1.5 text-[11px] font-bold text-gray-800 leading-tight">
                    Referral
                  </span>
                  <span className="text-[9px] font-medium text-amber-700">
                    Rewards
                  </span>
                </button>
              </div>
            </div>

            {/* Scrollable content */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {/* Quick links */}
              <div className="px-4 py-3">
                <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  Quick Links
                </p>
                {[
                  { label: "Home", Icon: FaHome, path: "/" },
                  { label: "All Products", Icon: FaShoppingCart, path: "/products" },
                  { label: "My Orders", Icon: FaBoxOpen, path: "/account/orders", auth: true },
                  { label: "My Address", Icon: FaMapMarkerAlt, path: "/account/addresses", auth: true },
                  { label: "Notifications", Icon: FaBell, path: "/account/notifications", auth: true, badge: unreadNotificationsCount },
                  { label: "Refer & Earn", Icon: FaGift, path: "/account/referral", auth: true, highlight: true },
                  { label: "Returns & Refunds", Icon: FaUndo, path: "/account/returns", auth: true },
                  { label: "Track Order", Icon: FaTruck, path: "/account/track" },
                  { label: "Wishlist", Icon: FaHeart, path: "/account/wishlist" },
                  { label: "Subscription", Icon: FaSyncAlt, path: "/account/subscriptions", auth: true },
                  { label: "My Account", Icon: FaUser, path: "/account/profile", auth: true },
                  { label: "About Us", Icon: FaInfoCircle, path: "/about" },
                  { label: "Contact Us", Icon: FaPhoneAlt, path: "/contact" },
                ].map(({ label, Icon, path, auth, badge, highlight }) => (
                  <button
                    key={label}
                    onClick={() => (auth ? handleAccountNav(path) : go(path))}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition active:scale-[0.98] ${
                      highlight
                        ? "bg-amber-50/60 text-amber-900 hover:bg-amber-100/70 font-bold"
                        : "text-gray-700 hover:bg-primary-50 hover:text-primary-700"
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 shrink-0 ${
                        highlight ? "text-amber-600" : "text-primary-600"
                      }`}
                      aria-hidden="true"
                    />
                    <span className="truncate">{label}</span>
                    {badge > 0 && (
                      <span className="ml-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#046a38] px-1 text-[10px] font-bold text-white">
                        {badge}
                      </span>
                    )}
                    {highlight && (
                      <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-white">
                        Reward
                      </span>
                    )}
                    <svg
                      className="ml-auto h-4 w-4 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                ))}
              </div>

              {/* Categories */}
              {categories.length > 0 && (
                <div className="border-t border-gray-100 px-4 py-3">
                  <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                    Categories
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.category_id}
                        onClick={() => handleCategoryClick(cat.slug)}
                        className="flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-left text-xs font-semibold text-gray-700 transition hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700"
                      >
                        {cat.image ? (
                          <img
                            src={cat.image}
                            alt={cat.name}
                            className="h-5 w-5 object-contain"
                          />
                        ) : (
                          <FaLeaf className="h-4 w-4 text-primary-600" aria-hidden="true" />
                        )}
                        <span className="truncate">{cat.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Location */}
              <div className="border-t border-gray-100 px-4 py-3">
                <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  Delivery Location
                </p>
                <LocationSelector
                  value={locationLabel || 'Set Location'}
                  onChange={() => { openLocationModal(); setMobileOpen(false); }}
                  disabled={Boolean(defaultAddress)}
                />
              </div>
            </div>

            {/* Drawer footer */}
            <div className="border-t border-gray-100 px-4 py-3">
              {isAuthenticated ? (
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-50 py-2.5 text-sm font-bold text-red-500 hover:bg-red-100"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Logout
                </button>
              ) : (
                <p className="text-center text-xs text-gray-400">
                  © 2024 KKisan Fresh
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Mobile Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center px-5"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
          onClick={() => setShowLogoutConfirm(false)}
        >
          <div
            className="w-full max-w-xs rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div className="mb-4 flex items-center justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
                <svg
                  className="h-7 w-7 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              </div>
            </div>
            {/* Title & Message */}
            <h2 className="mb-1 text-center text-base font-bold text-gray-800">
              Logout
            </h2>
            <p className="mb-6 text-center text-sm text-gray-500">
              Are you sure you want to logout?
            </p>
            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 active:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowLogoutConfirm(false);
                  logout();
                  go("/");
                }}
                className="flex-1 rounded-xl bg-red-500 py-2.5 text-sm font-bold text-white hover:bg-red-600 active:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
